'use client';

import { FC, useState, useCallback, useMemo } from 'react';
import { Box, Button, Divider, Typography } from '@mui/material';
import { StandardDialog } from '@/components/common/StandardDialog';
import { OptionKey } from '@/lib/options/optionTypes';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { useAuthStore } from '@/stores/authStore';
import { EntityAutocompleteWithLookup } from '@/components/common/EntityAutocompleteWithLookup';
import { useDialogStore, type BulkTransferDialogResult } from '@/stores/dialogStore';
import { useUserInteraction } from '@/hooks/useUnsavedChanges';
import { BulkSampleDto, PrivilegeEnum, ProjectDto, ReportingCategoryDto, StatusDto } from '@/api/ApiClient';
import { useBackendStore } from '@/stores/backendStore';
import { mayClearAllProjectsForSamples } from '@/lib/auth/unassignedSampleAccess';
import { LABEL_SUFFIX_OPTIONAL, LABEL_SUFFIX_REQUIRED } from '@/lib/formLabels';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { createProjectResolver } from '@/lib/utils/sampleProjectMultiplicity';
import {
  resolveReportingCategoryForTransfer,
  summarizeBulkReportingCategoryTransfer,
  TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE,
  validateTransferTargetProjectsWithBackend,
  type BulkTransferSamplePreflight,
} from '@/lib/utils/sampleTransferPreflight';

const OPTION_KEYS: OptionKey[] = ['projects', 'projectType', 'sampleReportingCategory'];

function bulkSamplesToPreflightInputs(
  samples: BulkSampleDto[],
  reportingCategories: readonly ReportingCategoryDto[]
): BulkTransferSamplePreflight[] {
  return samples.map((s) => ({
    sample: {
      id: s.id,
      sampleLabel: s.sampleLabel,
      projectIds: s.projectId != null ? [s.projectId] : [],
      reportingCategory: resolveReportingCategoryForTransfer(s, reportingCategories),
    },
    canUpdateSample: s.canUpdateSample === true,
  }));
}

/**
 * Dialog for transferring multiple bulk samples to target projects
 * Based on AddTransferActivityDialog but adapted for bulk transfers
 * Uses dialogStore to manage state
 *
 * Bulk transfer is limited to a single target project for now (bulk APIs assume at most one project).
 */
export const BulkTransferDialog: FC = () => {
  const { bulkTransferDialog, closeBulkTransferDialog } = useDialogStore();
  const { options } = useSelectOptions(OPTION_KEYS);
  const { showSnackbar } = useSnackbar();
  const { showProjectSelectionDialog } = useDialogStore();
  const backend = useBackendStore((state) => state.getBackend());

  const { samples, isCurrentProjectInHouse } = bulkTransferDialog;
  const isOpen = bulkTransferDialog.isOpen;
  const isAuthorized = useAuthStore((s) => s.isAuthorized);
  const currentUser = useAuthStore((s) => s.currentUser);
  const userCanClearAllProjects = mayClearAllProjectsForSamples(isAuthorized, samples ?? [], currentUser);
  const hasGlobalTransferCreate = isAuthorized(PrivilegeEnum.TransferActivity, 'create');
  const reportingCategories = options.sampleReportingCategory ?? [];

  const resolveProject = useMemo(
    () => createProjectResolver(options.projects, options.projectType),
    [options.projects, options.projectType]
  );

  // Form state — single target project only for bulk transfer
  const [selectedTargetProject, setSelectedTargetProject] = useState<{ id: number; label: string } | null>(null);
  const [createActivity, setCreateActivity] = useState<boolean>(true);
  const [projectError, setProjectError] = useState(false);
  const [reportingConfirmMessage, setReportingConfirmMessage] = useState<string | null>(null);
  const { hasUnsavedChanges, markAsInteracted } = useUserInteraction();

  const resetFormState = useCallback(() => {
    setSelectedTargetProject(null);
    setCreateActivity(true);
    setProjectError(false);
    setReportingConfirmMessage(null);
  }, []);

  // Reset form and close
  const handleClose = useCallback(() => {
    resetFormState();
    closeBulkTransferDialog(null);
  }, [closeBulkTransferDialog, resetFormState]);

  // Project selection handler
  const handleProjectLookup = async () => {
    const projects = await showProjectSelectionDialog('Select Target Project', { allowMultiple: false });
    if (projects && projects.length > 0) {
      const p = projects[0];
      setSelectedTargetProject({ id: p.id!, label: p.projectLabel! });
      markAsInteracted();
    }
  };

  const finishWithResult = (reportingCategoryChangeConfirmed: boolean) => {
    const result: BulkTransferDialogResult = {
      targetProjectLabels: selectedTargetProject ? [selectedTargetProject.label] : [],
      withoutActivity: !createActivity,
      ...(reportingCategoryChangeConfirmed ? { reportingCategoryChangeConfirmed: true } : {}),
    };
    resetFormState();
    closeBulkTransferDialog(result);
  };

  const handleSubmit = async () => {
    let hasError = false;

    if (!selectedTargetProject) {
      if (!userCanClearAllProjects) {
        setProjectError(true);
        hasError = true;
      } else {
        setProjectError(false);
      }
    } else {
      setProjectError(false);
    }

    if (hasError) {
      showSnackbar(
        !selectedTargetProject && !userCanClearAllProjects
          ? 'You are not authorized to remove all projects from these samples.'
          : 'Please select a target project.',
        'error'
      );
      return;
    }

    if (!samples?.length) {
      return;
    }

    try {
      const targetProjects: ProjectDto[] = selectedTargetProject
        ? [resolveProject(selectedTargetProject.id)].filter((p): p is ProjectDto => p != null)
        : [];

      if (selectedTargetProject) {
        const targetPreflight = await validateTransferTargetProjectsWithBackend(
          [selectedTargetProject],
          hasGlobalTransferCreate,
          backend
        );
        if (targetPreflight.kind === 'forbidden') {
          showSnackbar(targetPreflight.message, 'error');
          return;
        }
      }

      const preflightSamples = bulkSamplesToPreflightInputs(samples, reportingCategories);
      const summary = summarizeBulkReportingCategoryTransfer(
        preflightSamples,
        { reportingCategories, resolveProject },
        targetProjects
      );

      switch (summary.kind) {
        case 'blocked':
          showSnackbar(summary.message, 'error');
          return;
        case 'confirm-then-transfer':
          setReportingConfirmMessage(summary.message);
          return;
        case 'transfer-only':
          finishWithResult(false);
          return;
      }
    } catch (error: unknown) {
      showSnackbar(getProblemDetail(error) ?? 'Failed to prepare bulk transfer.', 'error');
    }
  };

  const handleConfirmReportingCategory = () => {
    finishWithResult(true);
  };

  const isReportingConfirmStep = reportingConfirmMessage != null;

  if (!samples || samples.length === 0) {
    return null;
  }

  const sampleCount = samples.length;
  const sampleLabels = samples
    .map((s) => s.sampleLabel)
    .filter(Boolean)
    .slice(0, 5);
  const remainingCount = Math.max(0, sampleCount - 5);

  return (
    <StandardDialog
      open={isOpen}
      onClose={handleClose}
      title={
        isReportingConfirmStep
          ? TRANSFER_REPORTING_CATEGORY_DIALOG_TITLE
          : `Transfer ${sampleCount} Sample${sampleCount !== 1 ? 's' : ''} to Another Project`
      }
      maxWidth="md"
      onSubmit={isReportingConfirmStep ? undefined : handleSubmit}
      submitText="Transfer Samples"
      showDefaultActions={!isReportingConfirmStep}
      hasUnsavedChanges={isReportingConfirmStep ? false : hasUnsavedChanges}
      unsavedChangesMessage="You have unsaved changes. Are you sure you want to discard them?"
      actions={
        isReportingConfirmStep ? (
          <>
            <Button
              variant="text"
              onClick={() => {
                setReportingConfirmMessage(null);
              }}
            >
              Back
            </Button>
            <Button variant="contained" onClick={handleConfirmReportingCategory}>
              Change category and transfer
            </Button>
          </>
        ) : undefined
      }
    >
      {isReportingConfirmStep ? (
        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
          {reportingConfirmMessage}
        </Typography>
      ) : (
        <>
          {/* Sample info */}
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Transferring {sampleCount} sample{sampleCount !== 1 ? 's' : ''}:
            </Typography>
            <Box component="ul" sx={{ pl: 3, mt: 1, mb: 2 }}>
              {sampleLabels.map((label, idx) => (
                <li key={idx}>
                  <Typography variant="body2">{label}</Typography>
                </li>
              ))}
              {remainingCount > 0 ? (
                <li>
                  <Typography variant="body2">... and {remainingCount} more</Typography>
                </li>
              ) : null}
            </Box>
          </Box>

          <Divider />

          {/* Target project: bulk flow supports at most one. Empty = remove from all projects; requires AccessUnassignedSample. */}
          <EntityAutocompleteWithLookup
            label={`Target Project${userCanClearAllProjects ? LABEL_SUFFIX_OPTIONAL : LABEL_SUFFIX_REQUIRED}`}
            options={
              options.projects
                ?.filter((p) => p.id && p.projectLabel && p.status === StatusDto.Submitted)
                ?.map((p) => ({ id: p.id!, label: p.projectLabel! })) ?? []
            }
            value={selectedTargetProject}
            onChange={(newValue) => {
              const v = newValue as { id: number; label: string } | null;
              setSelectedTargetProject(v);
              if (v) setProjectError(false);
              markAsInteracted();
            }}
            onLookupClick={handleProjectLookup}
            error={projectError}
            helperText={
              projectError
                ? 'You are not authorized to remove all projects from these samples.'
                : userCanClearAllProjects
                  ? 'Leave empty to remove from all projects.'
                  : undefined
            }
            lookupTooltip="Search and select a project"
            fullWidth
          />

          {/* Create Activity Checkbox - Only show for in-house projects */}
          {isCurrentProjectInHouse ? (
            <Box display="flex" alignItems="center">
              <input
                type="checkbox"
                id="createActivity"
                checked={createActivity}
                onChange={(e) => {
                  setCreateActivity(e.target.checked);
                  markAsInteracted();
                }}
                style={{ marginRight: '8px' }}
              />
              <label htmlFor="createActivity">Create transfer activity record</label>
            </Box>
          ) : null}
        </>
      )}
    </StandardDialog>
  );
};
